/**
 * P1.2: AKU v2 Manager Component
 * 
 * Claim-based Atomic Knowledge Units - structured, auditable knowledge objects.
 * 
 * Features:
 * - List/Filter AKUs by exam, status, normative_status, concept
 * - Detail view: Claim, Anchors, Evidence, Rule Trace
 * - Actions: Approve, Reject, Deprecate
 * - Promote from ontology_proposals
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Archive,
  AlertTriangle,
  Shield,
  ShieldCheck,
  ShieldAlert,
  BookMarked,
  Link2,
  ChevronDown,
  ChevronUp,
  History,
  User,
  Calendar,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';
import AkuActionDialog from './aku/AkuActionDialog';
import AkuStatsOverview from './aku/AkuStatsOverview';
import AkuFilterBar from './aku/AkuFilterBar';
import AkuPromoteDialog from './aku/AkuPromoteDialog';

const API = window.location.origin;

const getAuthHeaders = () => adminFetchHeaders(true);

// Normative status configuration
const NORMATIVE_STATUS_CONFIG = {
  SUPPORTED_PRIMARY: { 
    color: 'bg-green-100 text-green-800 border-green-300', 
    icon: ShieldCheck, 
    label: 'PRIMARY',
    description: 'Von PRIMARY-Quellen gestützt'
  },
  SUPPORTED_SECONDARY: { 
    color: 'bg-blue-100 text-blue-800 border-blue-300', 
    icon: Shield, 
    label: 'SECONDARY',
    description: 'Von SECONDARY-Quellen gestützt'
  },
  TRAINING_ONLY: { 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300', 
    icon: BookMarked, 
    label: 'TRAINING',
    description: 'Nur für Training (nicht-effektive Quelle)'
  },
  CONFLICTING: { 
    color: 'bg-red-100 text-red-800 border-red-300', 
    icon: ShieldAlert, 
    label: 'KONFLIKT',
    description: 'Quelle in ungelöstem Konflikt'
  },
  AMBIGUOUS: { 
    color: 'bg-orange-100 text-orange-800 border-orange-300', 
    icon: AlertTriangle, 
    label: 'MEHRDEUTIG',
    description: 'Widersprüchliche Quellen'
  }
};

// Status configuration
const STATUS_CONFIG = {
  DRAFT: { color: 'bg-gray-100 text-gray-800', label: 'Entwurf' },
  READY: { color: 'bg-green-100 text-green-800', label: 'Bereit' },
  REJECTED: { color: 'bg-red-100 text-red-800', label: 'Abgelehnt' },
  DEPRECATED: { color: 'bg-purple-100 text-purple-800', label: 'Veraltet' }
};

const AKUV2Manager = () => {
  const [akus, setAkus] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState('PSM1');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterNormative, setFilterNormative] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAku, setExpandedAku] = useState(null);
  
  // Action dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, akuId: null });
  const [actionReason, setActionReason] = useState('');
  
  // Promote dialog
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [promoteExam, setPromoteExam] = useState('PSM1');
  const [promoteForce, setPromoteForce] = useState(false);
  const [includeNonEffective, setIncludeNonEffective] = useState(false);
  
  // Claim workflow state
  const [claimStats, setClaimStats] = useState(null);
  const [preparingClaims, setPreparingClaims] = useState(false);
  
  // Async promotion job state
  const [promotionJob, setPromotionJob] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  
  const exams = ['PSM1', 'PSM2', 'PSPO1', 'SAFe', 'ITIL', 'PRINCE2', 'PMP', 'CAPM'];

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/akus-v2/stats?exam_profile_id=${selectedExam}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [selectedExam]);

  // Fetch claim workflow stats for promote dialog
  const fetchClaimStats = useCallback(async (examId) => {
    try {
      // Get DRAFT claims
      const draftRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${examId}&proposal_kind=CLAIM&status=DRAFT&page_size=1`,
        { headers: getAuthHeaders() }
      );
      const draftData = await draftRes.json();
      
      // Get REVIEW claims
      const reviewRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${examId}&proposal_kind=CLAIM&status=REVIEW&page_size=1`,
        { headers: getAuthHeaders() }
      );
      const reviewData = await reviewRes.json();
      
      // Get APPROVED claims
      const approvedRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${examId}&proposal_kind=CLAIM&status=APPROVED&page_size=1`,
        { headers: getAuthHeaders() }
      );
      const approvedData = await approvedRes.json();
      
      // Get MERGED claims
      const mergedRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${examId}&proposal_kind=CLAIM&status=MERGED&page_size=1`,
        { headers: getAuthHeaders() }
      );
      const mergedData = await mergedRes.json();
      
      setClaimStats({
        draft: draftData.total_count || 0,
        review: reviewData.total_count || 0,
        approved: approvedData.total_count || 0,
        merged: mergedData.total_count || 0
      });
    } catch (error) {
      console.error('Error fetching claim stats:', error);
      setClaimStats(null);
    }
  }, []);

  // Prepare claims: Submit -> Approve -> Merge all in one action
  const handlePrepareClaims = async () => {
    setPreparingClaims(true);
    const runKey = `manual_${Date.now().toString(36)}`;
    
    try {
      // Step 1: Get all DRAFT claims
      const draftRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${promoteExam}&proposal_kind=CLAIM&status=DRAFT&page_size=500`,
        { headers: getAuthHeaders() }
      );
      const draftData = await draftRes.json();
      const draftIds = (draftData.items || []).map(p => p.proposal_id);
      
      if (draftIds.length > 0) {
        // Submit all DRAFT -> REVIEW
        toast.info(`Submitting ${draftIds.length} Claims...`);
        const submitRes = await fetch(`${API}/api/ontology-proposals/bulk/submit-for-review`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ proposal_ids: draftIds, run_key: runKey })
        });
        if (!submitRes.ok) {
          const err = await submitRes.json();
          throw new Error(err.detail || 'Submit failed');
        }
      }
      
      // Step 2: Get all REVIEW claims and approve them
      const reviewRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${promoteExam}&proposal_kind=CLAIM&status=REVIEW&page_size=500`,
        { headers: getAuthHeaders() }
      );
      const reviewData = await reviewRes.json();
      const reviewIds = (reviewData.items || []).map(p => p.proposal_id);
      
      if (reviewIds.length > 0) {
        toast.info(`Approving ${reviewIds.length} Claims...`);
        const approveRes = await fetch(`${API}/api/ontology-proposals/bulk/approve`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ proposal_ids: reviewIds, run_key: runKey })
        });
        if (!approveRes.ok) {
          const err = await approveRes.json();
          throw new Error(err.detail || 'Approve failed');
        }
      }
      
      // Step 3: Get all APPROVED claims and merge them
      const approvedRes = await fetch(
        `${API}/api/ontology-proposals?exam_ids=${promoteExam}&proposal_kind=CLAIM&status=APPROVED&page_size=500`,
        { headers: getAuthHeaders() }
      );
      const approvedData = await approvedRes.json();
      const approvedIds = (approvedData.items || []).map(p => p.proposal_id);
      
      if (approvedIds.length > 0) {
        toast.info(`Merging ${approvedIds.length} Claims...`);
        const mergeRes = await fetch(`${API}/api/ontology-proposals/bulk/merge`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ proposal_ids: approvedIds, run_key: runKey })
        });
        if (!mergeRes.ok) {
          const err = await mergeRes.json();
          throw new Error(err.detail || 'Merge failed');
        }
        const mergeData = await mergeRes.json();
        toast.success(`${mergeData.merged_count || approvedIds.length} Claims gemerged!`);
      }
      
      // Refresh stats
      await fetchClaimStats(promoteExam);
      
    } catch (error) {
      toast.error('Claim Workflow fehlgeschlagen: ' + error.message);
    } finally {
      setPreparingClaims(false);
    }
  };

  // Fetch AKUs
  const fetchAkus = useCallback(async () => {
    try {
      let url = `${API}/api/akus-v2?exam_profile_id=${selectedExam}&limit=100`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterNormative) url += `&normative_status=${filterNormative}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // API returns paginated response: { items: [...], total_count, ... }
        setAkus(data.items || data || []);
      }
    } catch (error) {
      console.error('Error fetching AKUs:', error);
    }
  }, [selectedExam, filterStatus, filterNormative, searchTerm]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchAkus()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchAkus]);

  // Approve AKU
  const handleApprove = async () => {
    try {
      const response = await fetch(`${API}/api/akus-v2/${actionDialog.akuId}/approve?comment=${encodeURIComponent(actionReason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('AKU genehmigt');
        setActionDialog({ open: false, type: null, akuId: null });
        setActionReason('');
        await Promise.all([fetchStats(), fetchAkus()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Reject AKU
  const handleReject = async () => {
    if (!actionReason.trim()) {
      toast.error('Begründung ist erforderlich');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/akus-v2/${actionDialog.akuId}/reject?reason=${encodeURIComponent(actionReason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('AKU abgelehnt');
        setActionDialog({ open: false, type: null, akuId: null });
        setActionReason('');
        await Promise.all([fetchStats(), fetchAkus()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Deprecate AKU
  const handleDeprecate = async () => {
    if (!actionReason.trim()) {
      toast.error('Begründung ist erforderlich');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/akus-v2/${actionDialog.akuId}/deprecate?reason=${encodeURIComponent(actionReason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('AKU als veraltet markiert');
        setActionDialog({ open: false, type: null, akuId: null });
        setActionReason('');
        await Promise.all([fetchStats(), fetchAkus()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Promote all merged proposals - ASYNC VERSION
  const handlePromoteAll = async () => {
    try {
      // Submit async job instead of sync request
      const response = await fetch(`${API}/api/aku-v2/promotions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          exam_profile_id: promoteExam,
          force_training_only: promoteForce,
          include_non_effective: includeNonEffective
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Job gestartet: ${data.job_id}`);
        
        // Start polling for progress
        setPromotionJob({
          job_id: data.job_id,
          status: data.status,
          run_key: data.run_key,
          progress: { processed: 0, total: 0 },
          counts: { promoted_count: 0, skipped_count: 0, error_count: 0 },
          skipped_reasons: {}
        });
        setIsPolling(true);
        
        // Start polling
        pollJobStatus(data.job_id);
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Fehler bei der Promotion');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Poll job status
  const pollJobStatus = async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/api/aku-v2/promotions/${jobId}`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setPromotionJob(data);
          
          // Check if job is complete
          if (data.status === 'SUCCEEDED' || data.status === 'FAILED') {
            clearInterval(pollInterval);
            setIsPolling(false);
            
            if (data.status === 'SUCCEEDED') {
              const promoted = data.counts?.promoted_count || 0;
              const skipped = data.counts?.skipped_count || 0;
              const errors = data.counts?.error_count || 0;
              
              if (promoted > 0) {
                toast.success(`${promoted} AKUs promotet`);
              }
              if (skipped > 0 || errors > 0) {
                toast.info(`${skipped} übersprungen, ${errors} Fehler`);
              }
              if (promoted === 0 && data.admin_hint) {
                toast.warning(data.admin_hint, { duration: 8000 });
              }
              
              // Refresh data
              await Promise.all([fetchStats(), fetchAkus()]);
            } else {
              toast.error(data.admin_hint || 'Job fehlgeschlagen');
            }
          }
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 1000); // Poll every second
    
    // Store interval ID for cleanup
    return () => clearInterval(pollInterval);
  };

  // Render promotion progress
  const renderPromotionProgress = () => {
    if (!promotionJob) return null;
    
    const progress = promotionJob.progress || {};
    const counts = promotionJob.counts || {};
    const skipped = promotionJob.skipped_reasons || {};
    const timestamps = promotionJob.timestamps || {};
    
    const total = progress.total || 0;
    const processed = progress.processed || 0;
    const percentComplete = total > 0 ? Math.round((processed / total) * 100) : 0;
    
    const statusColors = {
      QUEUED: 'bg-yellow-500',
      RUNNING: 'bg-blue-500',
      SUCCEEDED: 'bg-green-500',
      FAILED: 'bg-red-500'
    };
    
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border" data-testid="promotion-progress">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${statusColors[promotionJob.status] || 'bg-gray-500'} ${promotionJob.status === 'RUNNING' ? 'animate-pulse' : ''}`}></div>
            <span className="font-medium">{promotionJob.status}</span>
            <span className="text-xs text-gray-500">Job: {promotionJob.job_id}</span>
          </div>
          {promotionJob.run_key && (
            <span className="text-xs text-gray-400">run_key: {promotionJob.run_key}</span>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Fortschritt: {processed} / {total}</span>
            <span>{percentComplete}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${statusColors[promotionJob.status] || 'bg-blue-500'}`}
              style={{ width: `${percentComplete}%` }}
            ></div>
          </div>
        </div>
        
        {/* Counts */}
        <div className="grid grid-cols-3 gap-2 text-sm mb-3">
          <div className="text-center p-2 bg-white rounded border">
            <div className="text-lg font-bold text-green-600">{counts.promoted_count || 0}</div>
            <div className="text-xs text-gray-500">Promotet</div>
          </div>
          <div className="text-center p-2 bg-white rounded border">
            <div className="text-lg font-bold text-yellow-600">{counts.skipped_count || 0}</div>
            <div className="text-xs text-gray-500">Übersprungen</div>
          </div>
          <div className="text-center p-2 bg-white rounded border">
            <div className="text-lg font-bold text-red-600">{counts.error_count || 0}</div>
            <div className="text-xs text-gray-500">Fehler</div>
          </div>
        </div>
        
        {/* Skip Reasons */}
        {(counts.skipped_count > 0 || counts.error_count > 0) && (
          <div className="text-xs space-y-1 p-2 bg-yellow-50 rounded border border-yellow-200">
            <p className="font-medium text-yellow-800">Skip-Gründe:</p>
            {skipped.dedupe > 0 && <p className="text-yellow-700">• Duplikat: {skipped.dedupe}</p>}
            {skipped.non_effective > 0 && <p className="text-yellow-700">• Nicht-effektiv: {skipped.non_effective}</p>}
            {skipped.orphan_anchors > 0 && <p className="text-yellow-700">• Orphan Anchors: {skipped.orphan_anchors}</p>}
            {skipped.status_not_allowed > 0 && <p className="text-yellow-700">• Falscher Status: {skipped.status_not_allowed}</p>}
            {skipped.version_mismatch > 0 && <p className="text-yellow-700">• Version Mismatch: {skipped.version_mismatch}</p>}
            {skipped.scope_mismatch > 0 && <p className="text-yellow-700">• Scope Mismatch: {skipped.scope_mismatch}</p>}
          </div>
        )}
        
        {/* Timestamps */}
        <div className="mt-2 text-xs text-gray-400 flex gap-4">
          {timestamps.submitted_at && <span>Gestartet: {new Date(timestamps.submitted_at).toLocaleTimeString('de-DE')}</span>}
          {timestamps.finished_at && <span>Fertig: {new Date(timestamps.finished_at).toLocaleTimeString('de-DE')}</span>}
        </div>
        
        {/* Admin Hint */}
        {promotionJob.admin_hint && promotionJob.status !== 'RUNNING' && (
          <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-sm text-blue-800">
            {promotionJob.admin_hint}
          </div>
        )}
      </div>
    );
  };

  // Render audit trail
  const renderAuditTrail = (auditTrail) => {
    if (!auditTrail || auditTrail.length === 0) return null;
    
    return (
      <div className="mt-4 border-t pt-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <History className="w-4 h-4" />
          Audit Trail
        </div>
        <div className="space-y-2">
          {auditTrail.slice(-5).reverse().map((entry, idx) => (
            <div key={`audit-${entry.timestamp || idx}-${entry.action}`} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{entry.action}</Badge>
                {entry.previous_status && (
                  <span className="text-gray-500">
                    {entry.previous_status} → {entry.new_status}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {entry.actor}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(entry.timestamp).toLocaleString('de-DE')}
                </span>
              </div>
              {entry.comment && (
                <p className="mt-1 text-gray-600 italic">"{entry.comment}"</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-green-500" />
        <span className="ml-2">Lade AKU v2...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-green-600" />
            AKU v2 - Claim-based Knowledge Units
          </h2>
          <p className="text-gray-600 mt-1">
            Strukturierte, auditierbare Wissenseinheiten (P1.2)
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedExam} onValueChange={setSelectedExam}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {exams.map(exam => (
                <SelectItem key={exam} value={exam}>{exam}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => Promise.all([fetchStats(), fetchAkus()])}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => {
            setShowPromoteDialog(true);
            fetchClaimStats(promoteExam);
          }} data-testid="promote-btn">
            <Upload className="w-4 h-4 mr-2" />
            Promoten
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <AkuStatsOverview
        stats={stats}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
      />

      {/* Filters */}
      <AkuFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        filterNormative={filterNormative}
        onFilterNormativeChange={setFilterNormative}
      />

      {/* AKU List */}
      <Card>
        <CardContent className="pt-4">
          {/* P2: Header with count */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b">
            <span className="text-sm font-medium text-gray-600">
              {akus.length} AKUs für {selectedExam}
            </span>
          </div>
          {akus.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Keine AKUs gefunden.</p>
              <p className="text-sm mt-2">Promoten Sie Proposals um AKUs zu erstellen.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="akus-list">
              {akus.map((aku) => {
                const normativeConfig = NORMATIVE_STATUS_CONFIG[aku.normative_status] || NORMATIVE_STATUS_CONFIG.TRAINING_ONLY;
                const statusConfig = STATUS_CONFIG[aku.status] || STATUS_CONFIG.DRAFT;
                const NormativeIcon = normativeConfig.icon;
                const isExpanded = expandedAku === aku.aku_id;
                const isBlocked = ['CONFLICTING', 'AMBIGUOUS'].includes(aku.normative_status);
                
                return (
                  <div 
                    key={aku.aku_id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 ${isBlocked ? 'border-red-200 bg-red-50/30' : ''}`}
                    data-testid={`aku-${aku.aku_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={normativeConfig.color}>
                            <NormativeIcon className="w-3 h-3 mr-1" />
                            {normativeConfig.label}
                          </Badge>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {aku.ontology_version_number}
                          </Badge>
                          {/* Exam Badge - prominent display */}
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                            📚 {aku.exam_profile_id || selectedExam}
                          </Badge>
                          {/* Primary Source Badge */}
                          {aku.evidence_bundle?.[0]?.source_id && (
                            <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                              📄 {aku.evidence_bundle[0].source_id.slice(0, 15)}...
                            </Badge>
                          )}
                        </div>
                        
                        {/* Claim */}
                        <p className="text-sm font-medium mb-2">{aku.claim_text}</p>
                        
                        {/* Concept Anchors */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Link2 className="w-4 h-4 text-gray-400" />
                          {aku.concept_names?.map((name, idx) => (
                            <Badge key={`concept-${name}-${idx}`} variant="secondary" className="text-xs">
                              {name}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="text-xs text-gray-400">
                          {aku.aku_id} • {aku.evidence_bundle?.length || 0} Evidence-Refs
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {/* Actions based on status */}
                        {aku.status === 'DRAFT' && !isBlocked && (
                          <Button
                            size="sm"
                            onClick={() => setActionDialog({ open: true, type: 'approve', akuId: aku.aku_id })}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {aku.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => setActionDialog({ open: true, type: 'reject', akuId: aku.aku_id })}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {aku.status === 'READY' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setActionDialog({ open: true, type: 'deprecate', akuId: aku.aku_id })}
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedAku(isExpanded ? null : aku.aku_id)}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-4">
                        {/* Evidence Bundle */}
                        <div>
                          <div className="text-sm font-medium mb-2">Evidence Bundle:</div>
                          <div className="space-y-1">
                            {aku.evidence_bundle?.map((ev, idx) => (
                              <div key={ev.chunk_id || `evidence-${idx}`} className="text-xs bg-gray-50 p-2 rounded">
                                <span className="text-gray-500">Chunk:</span> {ev.chunk_id}
                                <span className="ml-3 text-gray-500">Source:</span> {ev.source_id}
                                {ev.quote_span && (
                                  <p className="mt-1 italic text-gray-600">"{ev.quote_span}"</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Rule Trace */}
                        {aku.rule_trace && (
                          <div>
                            <div className="text-sm font-medium mb-2">Rule Trace:</div>
                            <div className="text-xs bg-blue-50 p-2 rounded">
                              <p><strong>Normativ-Status Grund:</strong> {aku.rule_trace.normative_status_reason}</p>
                              {aku.rule_trace.effective_sources_used?.length > 0 && (
                                <p><strong>Effektive Quellen:</strong> {aku.rule_trace.effective_sources_used.join(', ')}</p>
                              )}
                              {Object.keys(aku.rule_trace.redirect_resolutions || {}).length > 0 && (
                                <p><strong>Redirects:</strong> {JSON.stringify(aku.rule_trace.redirect_resolutions)}</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Blocked Warning */}
                        {isBlocked && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded">
                            <div className="flex items-center gap-2 text-red-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">READY blockiert</span>
                            </div>
                            <p className="text-xs text-red-600 mt-1">
                              Normative Status {aku.normative_status} verhindert Genehmigung. 
                              Lösen Sie den Konflikt oder lehnen Sie ab.
                            </p>
                          </div>
                        )}
                        
                        {/* Audit Trail */}
                        {renderAuditTrail(aku.audit_trail)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AKU action dialogs (approve / reject / deprecate) */}
      <AkuActionDialog
        type="approve"
        open={actionDialog.open && actionDialog.type === 'approve'}
        onClose={() => setActionDialog({ open: false, type: null, akuId: null })}
        reason={actionReason}
        onReasonChange={setActionReason}
        onConfirm={handleApprove}
      />
      <AkuActionDialog
        type="reject"
        open={actionDialog.open && actionDialog.type === 'reject'}
        onClose={() => setActionDialog({ open: false, type: null, akuId: null })}
        reason={actionReason}
        onReasonChange={setActionReason}
        onConfirm={handleReject}
      />
      <AkuActionDialog
        type="deprecate"
        open={actionDialog.open && actionDialog.type === 'deprecate'}
        onClose={() => setActionDialog({ open: false, type: null, akuId: null })}
        reason={actionReason}
        onReasonChange={setActionReason}
        onConfirm={handleDeprecate}
      />

      {/* Promote Dialog */}
      <AkuPromoteDialog
        open={showPromoteDialog}
        onOpenChange={setShowPromoteDialog}
        exams={exams}
        promoteExam={promoteExam}
        onPromoteExamChange={(val) => {
          setPromoteExam(val);
          fetchClaimStats(val);
        }}
        claimStats={claimStats}
        onPrepareClaims={handlePrepareClaims}
        preparingClaims={preparingClaims}
        promoteForce={promoteForce}
        setPromoteForce={setPromoteForce}
        includeNonEffective={includeNonEffective}
        setIncludeNonEffective={setIncludeNonEffective}
        promotionJob={promotionJob}
        isPolling={isPolling}
        onPromoteAll={handlePromoteAll}
        onClose={() => {
          setShowPromoteDialog(false);
          setPromotionJob(null);
        }}
        progressNode={renderPromotionProgress()}
      />
    </div>
  );
};

export default AKUV2Manager;
